import { Message } from "@arco-design/web-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import { crmCollaborationTaskQueryRootKey } from "../config";
import {
  cancelCrmCollaborationTask,
  claimCrmCollaborationTaskItem,
  claimCrmCollaborationTaskItemsByOwner,
  confirmCrmCollaborationTaskImport,
  createCrmCollaborationTask,
  createCrmCollaborationTaskImport,
  fetchCrmCollaborationPoolItems,
  fetchCrmCollaborationPoolOriginalOwners,
  fetchCrmCollaborationPoolPublishers,
  fetchCrmCollaborationStaffOptions,
  fetchCrmCollaborationTaskList,
  fetchMyCrmCollaborationTaskPublishers,
  publishCrmCollaborationTask,
  releaseCrmCollaborationTaskItems,
  releaseCrmCollaborationTaskItemsByOwner,
  returnCrmCollaborationTaskItemToPool,
  returnCrmCollaborationTaskItemsToPoolByOwner,
} from "../repo/crmCollaborationTaskRepo";
import {
  createContactWorkbenchRecord,
  deleteContactWorkbenchRecord,
  fetchContactWorkbenchDetail,
  fetchContactWorkbenchList,
  fetchContactWorkbenchCollaborationContactCounts,
  fetchContactWorkbenchMetadata,
  fetchContactWorkbenchRecords,
  searchContactWorkbenchOrganizations,
  searchContactWorkbenchOrganizationUnits,
  updateContactWorkbenchDetail,
} from "../../crm_contact_workbench/repo/crmContactWorkbenchRepo";
import type {
  ContactWorkbenchRecordCreatePayload,
  ContactWorkbenchUpdatePayload,
} from "../../crm_contact_workbench/types";
import {
  formatCollaborationTaskError,
  isCollaborationTaskClaimConflict,
} from "../service/crmCollaborationTaskService";
import type {
  CrmCollaborationAssignmentMode,
  CrmCollaborationPoolListFilters,
  CrmCollaborationTargetType,
  CrmCollaborationTaskImportResult,
  CrmCollaborationTaskType,
} from "../types";

const DEFAULT_PAGE_SIZE = 20;
export const CRM_COLLABORATION_PAGE_SIZE_OPTIONS = [10, 20, 50, 100] as const;

export const EMPTY_CRM_COLLABORATION_POOL_FILTERS: CrmCollaborationPoolListFilters = {
  keyword: "",
  province: "",
  orgName: "",
  originalOwnerUserIds: [],
};

export type CrmCollaborationTaskDraft = {
  taskName: string;
  taskType: CrmCollaborationTaskType;
  targetType: CrmCollaborationTargetType;
  assignmentMode: CrmCollaborationAssignmentMode;
  ownerUserId: number;
  defaultAssignedUserId?: number;
  rawText: string;
  remark: string;
};

export type CrmCollaborationMyContactsTaskType = "all" | "tele_sales" | "strong_follow";

/** 编排协作任务页面的查询、导入校验和任务池操作。 */
export function useCrmCollaborationTaskRuntime(
  authenticatedUserId?: number,
  loadStaffOptions = false,
  loadMyContacts = false,
  myContactsTaskType: CrmCollaborationMyContactsTaskType = "all",
  onlyMyContacts = true,
) {
  const queryClient = useQueryClient();
  const currentUserId = authenticatedUserId ?? 0;
  const [taskPage, setTaskPage] = useState(1);
  const [teleSalesPage, setTeleSalesPage] = useState(1);
  const [teleSalesPageSize, setTeleSalesPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [teleSalesViewScope, setTeleSalesViewScope] = useState<"all" | "mine">("all");
  const [teleSalesFilters, setTeleSalesFilters] = useState<CrmCollaborationPoolListFilters>(
    EMPTY_CRM_COLLABORATION_POOL_FILTERS,
  );
  const [strongFollowPage, setStrongFollowPage] = useState(1);
  const [strongFollowPageSize, setStrongFollowPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [strongFollowFilters, setStrongFollowFilters] = useState<CrmCollaborationPoolListFilters>(
    EMPTY_CRM_COLLABORATION_POOL_FILTERS,
  );
  const [myItemsPage, setMyItemsPage] = useState(1);
  const [myItemsPageSize, setMyItemsPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [selectedContactId, setSelectedContactId] = useState<number>();
  const [draft, setDraft] = useState<CrmCollaborationTaskDraft>({
    taskName: "",
    taskType: "tele_sales",
    targetType: "contact",
    assignmentMode: "pool",
    ownerUserId: authenticatedUserId ?? 0,
    rawText: "",
    remark: "",
  });
  const [latestImportResult, setLatestImportResult] = useState<CrmCollaborationTaskImportResult>();
  const [latestTaskId, setLatestTaskId] = useState<number>();
  const [claimingItemId, setClaimingItemId] = useState<number>();
  const [batchClaiming, setBatchClaiming] = useState(false);
  const [ownerBatchClaiming, setOwnerBatchClaiming] = useState(false);
  const [releaseBatching, setReleaseBatching] = useState(false);
  const [ownerBatchReleasing, setOwnerBatchReleasing] = useState(false);
  const [returnBatching, setReturnBatching] = useState(false);
  const [ownerBatchReturning, setOwnerBatchReturning] = useState(false);
  const claimInFlightItemIdRef = useRef<number | undefined>(undefined);
  const batchClaimInFlightRef = useRef(false);
  const ownerBatchClaimInFlightRef = useRef(false);
  const releaseBatchInFlightRef = useRef(false);
  const ownerBatchReleaseInFlightRef = useRef(false);
  const returnBatchInFlightRef = useRef(false);
  const ownerBatchReturnInFlightRef = useRef(false);

  const staffOptionsQuery = useQuery({
    queryKey: [...crmCollaborationTaskQueryRootKey, "staff-options"],
    queryFn: fetchCrmCollaborationStaffOptions,
    enabled: loadStaffOptions,
  });
  const tasksQuery = useQuery({
    queryKey: [...crmCollaborationTaskQueryRootKey, "tasks", taskPage],
    queryFn: () => fetchCrmCollaborationTaskList({ page: taskPage, pageSize: DEFAULT_PAGE_SIZE }),
  });
  const teleSalesQuery = useQuery({
    queryKey: [
      ...crmCollaborationTaskQueryRootKey,
      "pool",
      "tele-sales",
      currentUserId,
      teleSalesPage,
      teleSalesPageSize,
      teleSalesViewScope,
      teleSalesFilters,
    ],
    queryFn: () =>
      fetchCrmCollaborationPoolItems({
        pool: "tele-sales",
        currentUserId,
        page: teleSalesPage,
        pageSize: teleSalesPageSize,
        viewScope: teleSalesViewScope,
        keyword: teleSalesFilters.keyword,
        province: teleSalesFilters.province,
        orgName: teleSalesFilters.orgName,
        originalOwnerUserIds: teleSalesFilters.originalOwnerUserIds,
      }),
    enabled: currentUserId > 0,
  });
  const strongFollowQuery = useQuery({
    queryKey: [
      ...crmCollaborationTaskQueryRootKey,
      "pool",
      "strong-follow",
      currentUserId,
      strongFollowPage,
      strongFollowPageSize,
      strongFollowFilters,
    ],
    queryFn: () =>
      fetchCrmCollaborationPoolItems({
        pool: "strong-follow",
        currentUserId,
        page: strongFollowPage,
        pageSize: strongFollowPageSize,
        keyword: strongFollowFilters.keyword,
        province: strongFollowFilters.province,
        orgName: strongFollowFilters.orgName,
        originalOwnerUserIds: strongFollowFilters.originalOwnerUserIds,
      }),
    enabled: currentUserId > 0,
  });
  const teleSalesPublishersQuery = useQuery({
    queryKey: [...crmCollaborationTaskQueryRootKey, "pool", "tele-sales", "publishers", currentUserId],
    queryFn: () => fetchCrmCollaborationPoolPublishers("tele_sales_pool"),
    enabled: currentUserId > 0,
  });
  const strongFollowPublishersQuery = useQuery({
    queryKey: [...crmCollaborationTaskQueryRootKey, "pool", "strong-follow", "publishers", currentUserId],
    queryFn: () => fetchCrmCollaborationPoolPublishers("strong_follow_pool"),
    enabled: currentUserId > 0,
  });
  const teleSalesOriginalOwnersQuery = useQuery({
    queryKey: [
      ...crmCollaborationTaskQueryRootKey,
      "pool",
      "tele-sales",
      "original-owners",
      currentUserId,
    ],
    queryFn: () => fetchCrmCollaborationPoolOriginalOwners("tele_sales_pool"),
    enabled: currentUserId > 0,
  });
  const strongFollowOriginalOwnersQuery = useQuery({
    queryKey: [
      ...crmCollaborationTaskQueryRootKey,
      "pool",
      "strong-follow",
      "original-owners",
      currentUserId,
    ],
    queryFn: () => fetchCrmCollaborationPoolOriginalOwners("strong_follow_pool"),
    enabled: currentUserId > 0,
  });
  const myContactsQuery = useQuery({
    queryKey: [
      ...crmCollaborationTaskQueryRootKey,
      "my-contacts",
      currentUserId,
      myItemsPage,
      myItemsPageSize,
      myContactsTaskType,
      onlyMyContacts,
    ],
    queryFn: () =>
      fetchContactWorkbenchList({
        page: myItemsPage,
        pageSize: myItemsPageSize,
        sortField: "last_contact_at",
        sortDirection: "desc",
        viewScope: "mine",
        collaborationScope: onlyMyContacts ? "mine" : "all",
        collaborationTaskType:
          myContactsTaskType === "all" ? undefined : myContactsTaskType,
      }),
    enabled: currentUserId > 0 && loadMyContacts,
  });
  const myContactsCountsQuery = useQuery({
    queryKey: [
      ...crmCollaborationTaskQueryRootKey,
      "my-contacts-counts",
      currentUserId,
      onlyMyContacts,
    ],
    queryFn: () =>
      fetchContactWorkbenchCollaborationContactCounts({
        collaborationScope: onlyMyContacts ? "mine" : "all",
      }),
    enabled: currentUserId > 0 && loadMyContacts,
  });
  const myItemPublishersQuery = useQuery({
    queryKey: [...crmCollaborationTaskQueryRootKey, "my-items", "publishers", currentUserId],
    queryFn: fetchMyCrmCollaborationTaskPublishers,
    enabled: currentUserId > 0 && loadMyContacts,
  });
  const contactMetadataQuery = useQuery({
    queryKey: ["crm_contact_workbench", "metadata", currentUserId],
    queryFn: fetchContactWorkbenchMetadata,
    enabled: currentUserId > 0 && loadMyContacts,
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
  });
  const contactDetailQuery = useQuery({
    queryKey: ["crm_contact_workbench", "contact-detail", selectedContactId],
    queryFn: () => fetchContactWorkbenchDetail(selectedContactId as number),
    enabled: Boolean(selectedContactId),
  });
  const contactRecordsQuery = useQuery({
    queryKey: ["crm_contact_workbench", "contact-records", selectedContactId],
    queryFn: () => fetchContactWorkbenchRecords(selectedContactId as number),
    enabled: Boolean(selectedContactId),
  });
  const updateContactMutation = useMutation({
    mutationFn: updateContactWorkbenchDetail,
    onSuccess: async () => {
      Message.success("联系人资料已保存");
      await queryClient.invalidateQueries({
        queryKey: ["crm_contact_workbench", "contact-detail", selectedContactId],
      });
      await queryClient.invalidateQueries({ queryKey: crmCollaborationTaskQueryRootKey });
    },
  });

  useEffect(() => {
    if (currentUserId <= 0 || draft.ownerUserId > 0) {
      return;
    }
    setDraft((current) => ({ ...current, ownerUserId: currentUserId }));
  }, [currentUserId, draft.ownerUserId]);

  /** 统一刷新协作任务相关查询缓存。 */
  async function invalidateTaskQueries() {
    await queryClient.invalidateQueries({ queryKey: crmCollaborationTaskQueryRootKey });
  }

  const createAndImportMutation = useMutation({
    mutationFn: async () => {
      const task = await createCrmCollaborationTask({
        taskName: draft.taskName,
        taskType: draft.taskType,
        targetType: draft.targetType,
        assignmentMode: draft.assignmentMode,
        ownerUserId: draft.ownerUserId,
        defaultAssignedUserId: draft.defaultAssignedUserId,
        sourceType: "pasted_ids",
        remark: draft.remark,
      });
      const importResult = await createCrmCollaborationTaskImport(task.id, {
        importType: "paste",
        rawText: draft.rawText,
        createdBy: currentUserId,
        remark: "",
      });
      return { task, importResult };
    },
    onSuccess: async ({ task, importResult }) => {
      setLatestTaskId(task.id);
      setLatestImportResult(importResult);
      Message.success("任务草稿已创建，导入校验预览已生成");
      await invalidateTaskQueries();
    },
  });

  const confirmImportMutation = useMutation({
    mutationFn: async () => {
      if (!latestTaskId || !latestImportResult) {
        throw new Error("请先创建任务并完成导入校验");
      }
      return confirmCrmCollaborationTaskImport(latestTaskId, latestImportResult.id, currentUserId);
    },
    onSuccess: async (result) => {
      Message.success(`确认导入完成：写入 ${result.insertedCount} 条，跳过 ${result.skippedCount} 条`);
      await invalidateTaskQueries();
    },
  });

  const publishMutation = useMutation({
    mutationFn: (taskId: number) => publishCrmCollaborationTask(taskId, currentUserId),
    onSuccess: async () => {
      Message.success("协作任务已发布");
      await invalidateTaskQueries();
    },
  });
  const cancelMutation = useMutation({
    mutationFn: (taskId: number) => cancelCrmCollaborationTask(taskId, currentUserId),
    onSuccess: async () => {
      Message.success("协作任务已取消");
      await invalidateTaskQueries();
    },
  });
  const claimMutation = useMutation({
    mutationFn: (itemId: number) => claimCrmCollaborationTaskItem(itemId, currentUserId),
    onSuccess: async () => {
      Message.success("任务对象已认领");
      await invalidateTaskQueries();
    },
    onError: async (error) => {
      if (isCollaborationTaskClaimConflict(error)) {
        await invalidateTaskQueries();
      }
    },
  });
  const batchClaimMutation = useMutation({
    mutationFn: async (itemIds: number[]) => {
      const results = await Promise.allSettled(itemIds.map((itemId) => claimCrmCollaborationTaskItem(itemId, currentUserId)));
      const failed = results.filter((result): result is PromiseRejectedResult => result.status === "rejected");
      return { claimedCount: results.length - failed.length, failedCount: failed.length };
    },
    onSuccess: async ({ claimedCount, failedCount }) => {
      if (failedCount > 0) {
        Message.warning(`批量认领完成：成功 ${claimedCount} 条，失败 ${failedCount} 条，请刷新后重试`);
      } else {
        Message.success(`已批量认领 ${claimedCount} 条任务对象`);
      }
      await invalidateTaskQueries();
    },
    onError: async () => {
      await invalidateTaskQueries();
    },
  });
  const ownerBatchClaimMutation = useMutation({
    mutationFn: ({
      poolCode,
      ownerUserIds,
    }: {
      poolCode: "tele_sales_pool" | "strong_follow_pool";
      ownerUserIds: number[];
    }) =>
      Promise.allSettled(
        ownerUserIds.map((ownerUserId) =>
          claimCrmCollaborationTaskItemsByOwner(poolCode, ownerUserId, currentUserId),
        ),
      ).then((results) => ({
        claimedCount: results.reduce(
          (total, result) =>
            total + (result.status === "fulfilled" ? result.value.claimedCount : 0),
          0,
        ),
        failedCount: results.filter((result) => result.status === "rejected").length,
      })),
    onSuccess: async ({ claimedCount, failedCount }) => {
      if (failedCount > 0) {
        Message.warning(
          `批量认领完成：成功 ${claimedCount} 条，${failedCount} 个发布人处理失败，请刷新后重试`,
        );
      } else if (claimedCount > 0) {
        Message.success(`已认领所选发布人的 ${claimedCount} 条任务对象`);
      } else {
        Message.info("所选发布人暂无可认领任务对象");
      }
      await invalidateTaskQueries();
    },
    onError: async (error) => {
      if (isCollaborationTaskClaimConflict(error)) {
        await invalidateTaskQueries();
      }
    },
  });
  const releaseMutation = useMutation({
    mutationFn: ({ taskId, itemIds }: { taskId: number; itemIds: number[] }) =>
      releaseCrmCollaborationTaskItems(taskId, itemIds, currentUserId),
    onSuccess: async (result) => {
      Message.success(`已释放 ${result.releasedCount} 条任务权限`);
      await invalidateTaskQueries();
    },
  });
  const releaseBatchMutation = useMutation({
    mutationFn: async (items: Array<{ taskId: number; itemId: number }>) => {
      const grouped = new Map<number, number[]>();
      for (const item of items) {
        const itemIds = grouped.get(item.taskId) ?? [];
        itemIds.push(item.itemId);
        grouped.set(item.taskId, itemIds);
      }
      const results = await Promise.allSettled(
        Array.from(grouped, ([taskId, itemIds]) =>
          releaseCrmCollaborationTaskItems(taskId, itemIds, currentUserId),
        ),
      );
      return {
        releasedCount: results.reduce(
          (total, result) => total + (result.status === "fulfilled" ? result.value.releasedCount : 0),
          0,
        ),
        failedCount: results.filter((result) => result.status === "rejected").length,
      };
    },
    onSuccess: async ({ releasedCount, failedCount }) => {
      if (failedCount > 0) {
        Message.warning(`批量释放完成：成功 ${releasedCount} 条，${failedCount} 个任务批次失败，请刷新后重试`);
      } else {
        Message.success(`已批量释放 ${releasedCount} 条任务权限`);
      }
      await invalidateTaskQueries();
    },
    onError: async () => {
      await invalidateTaskQueries();
    },
  });
  const ownerBatchReleaseMutation = useMutation({
    mutationFn: (ownerUserIds: number[]) =>
      Promise.allSettled(
        ownerUserIds.map((ownerUserId) =>
          releaseCrmCollaborationTaskItemsByOwner(ownerUserId, currentUserId),
        ),
      ).then((results) => ({
        releasedCount: results.reduce(
          (total, result) =>
            total + (result.status === "fulfilled" ? result.value.releasedCount : 0),
          0,
        ),
        failedCount: results.filter((result) => result.status === "rejected").length,
      })),
    onSuccess: async ({ releasedCount, failedCount }) => {
      if (failedCount > 0) {
        Message.warning(
          `批量释放完成：成功 ${releasedCount} 条，${failedCount} 个发布人处理失败，请刷新后重试`,
        );
      } else if (releasedCount > 0) {
        Message.success(`已释放所选发布人的 ${releasedCount} 条任务权限`);
      } else {
        Message.info("所选发布人暂无可释放任务对象");
      }
      await invalidateTaskQueries();
    },
    onError: async () => {
      await invalidateTaskQueries();
    },
  });
  const returnToPoolMutation = useMutation({
    mutationFn: (itemId: number) => returnCrmCollaborationTaskItemToPool(itemId),
    onSuccess: async () => {
      Message.success("任务已退回任务池，其他人可以重新认领");
      await Promise.all([
        invalidateTaskQueries(),
        queryClient.invalidateQueries({ queryKey: ["crm_contact_workbench"] }),
      ]);
    },
  });
  const returnBatchMutation = useMutation({
    mutationFn: async (itemIds: number[]) => {
      const results = await Promise.allSettled(
        itemIds.map((itemId) => returnCrmCollaborationTaskItemToPool(itemId)),
      );
      return {
        returnedCount: results.reduce(
          (total, result) =>
            total + (result.status === "fulfilled" ? result.value.returnedCount : 0),
          0,
        ),
        failedCount: results.filter((result) => result.status === "rejected").length,
      };
    },
    onSuccess: async ({ returnedCount, failedCount }) => {
      if (failedCount > 0) {
        Message.warning(
          `批量退回完成：成功 ${returnedCount} 条，失败 ${failedCount} 条，请刷新后重试`,
        );
      } else {
        Message.success(`已批量退回 ${returnedCount} 条任务到任务池`);
      }
      await Promise.all([
        invalidateTaskQueries(),
        queryClient.invalidateQueries({ queryKey: ["crm_contact_workbench"] }),
      ]);
    },
    onError: async () => {
      await invalidateTaskQueries();
    },
  });
  const ownerBatchReturnMutation = useMutation({
    mutationFn: (ownerUserIds: number[]) =>
      Promise.allSettled(
        ownerUserIds.map((ownerUserId) =>
          returnCrmCollaborationTaskItemsToPoolByOwner(ownerUserId, currentUserId),
        ),
      ).then((results) => ({
        returnedCount: results.reduce(
          (total, result) =>
            total + (result.status === "fulfilled" ? result.value.returnedCount : 0),
          0,
        ),
        failedCount: results.filter((result) => result.status === "rejected").length,
      })),
    onSuccess: async ({ returnedCount, failedCount }) => {
      if (failedCount > 0) {
        Message.warning(
          `批量退回完成：成功 ${returnedCount} 条，${failedCount} 个发布人处理失败，请刷新后重试`,
        );
      } else if (returnedCount > 0) {
        Message.success(`已退回所选发布人的 ${returnedCount} 条任务到任务池`);
      } else {
        Message.info("所选发布人暂无可退回任务池的任务对象");
      }
      await Promise.all([
        invalidateTaskQueries(),
        queryClient.invalidateQueries({ queryKey: ["crm_contact_workbench"] }),
      ]);
    },
    onError: async () => {
      await invalidateTaskQueries();
    },
  });
  const createRecordMutation = useMutation({
    mutationFn: createContactWorkbenchRecord,
    onSuccess: async (_, variables) => {
      Message.success("联系人动态已添加");
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["crm_contact_workbench", "contact-records", variables.contactId],
        }),
        queryClient.invalidateQueries({
          queryKey: [...crmCollaborationTaskQueryRootKey, "my-contacts"],
        }),
      ]);
    },
  });
  const deleteRecordMutation = useMutation({
    mutationFn: deleteContactWorkbenchRecord,
    onSuccess: async (_, variables) => {
      Message.success("联系人动态已删除");
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["crm_contact_workbench", "contact-records", variables.contactId],
        }),
        queryClient.invalidateQueries({
          queryKey: [...crmCollaborationTaskQueryRootKey, "my-contacts"],
        }),
      ]);
    },
  });
  const errorMessage =
    tasksQuery.error ||
    teleSalesQuery.error ||
    strongFollowQuery.error ||
    teleSalesPublishersQuery.error ||
    strongFollowPublishersQuery.error ||
    myContactsQuery.error ||
    myContactsCountsQuery.error ||
    myItemPublishersQuery.error ||
    contactMetadataQuery.error ||
    contactDetailQuery.error ||
    contactRecordsQuery.error
      ? "协作任务数据加载失败，请稍后重试。"
      : createAndImportMutation.error ||
          confirmImportMutation.error ||
          publishMutation.error ||
          cancelMutation.error ||
          claimMutation.error ||
          batchClaimMutation.error ||
          ownerBatchClaimMutation.error ||
          releaseMutation.error ||
          releaseBatchMutation.error ||
          ownerBatchReleaseMutation.error ||
          returnToPoolMutation.error ||
          returnBatchMutation.error ||
          ownerBatchReturnMutation.error ||
          updateContactMutation.error ||
          createRecordMutation.error ||
          deleteRecordMutation.error
        ? formatCollaborationTaskError(
            createAndImportMutation.error ??
              confirmImportMutation.error ??
              publishMutation.error ??
              cancelMutation.error ??
              claimMutation.error ??
              batchClaimMutation.error ??
              ownerBatchClaimMutation.error ??
              releaseMutation.error ??
              releaseBatchMutation.error ??
              ownerBatchReleaseMutation.error ??
              returnToPoolMutation.error ??
              returnBatchMutation.error ??
              ownerBatchReturnMutation.error ??
              updateContactMutation.error ??
              createRecordMutation.error ??
              deleteRecordMutation.error,
          )
        : "";

  /** 立即锁定认领动作，避免 React 提交状态生效前的连续点击重复发请求。 */
  async function handleClaimItem(itemId: number) {
    if (
      claimInFlightItemIdRef.current !== undefined ||
      batchClaimInFlightRef.current ||
      ownerBatchClaimInFlightRef.current ||
      releaseBatchInFlightRef.current ||
      ownerBatchReleaseInFlightRef.current
    ) {
      return;
    }
    claimInFlightItemIdRef.current = itemId;
    setClaimingItemId(itemId);
    try {
      await claimMutation.mutateAsync(itemId);
    } catch {
      // 错误由 mutation 状态统一展示，避免事件处理器产生未处理 Promise。
    } finally {
      claimInFlightItemIdRef.current = undefined;
      setClaimingItemId(undefined);
    }
  }

  return {
    currentUserId,
    taskPage,
    setTaskPage,
    teleSalesPage,
    setTeleSalesPage,
    teleSalesPageSize,
    setTeleSalesPageSize: (nextPageSize: number) => {
      setTeleSalesPageSize(nextPageSize);
      setTeleSalesPage(1);
    },
    teleSalesViewScope,
    setTeleSalesViewScope,
    teleSalesFilters,
    setTeleSalesFilters,
    strongFollowPage,
    setStrongFollowPage,
    strongFollowPageSize,
    setStrongFollowPageSize: (nextPageSize: number) => {
      setStrongFollowPageSize(nextPageSize);
      setStrongFollowPage(1);
    },
    strongFollowFilters,
    setStrongFollowFilters,
    myItemsPage,
    setMyItemsPage,
    myItemsPageSize,
    setMyItemsPageSize: (nextPageSize: number) => {
      setMyItemsPageSize(nextPageSize);
      setMyItemsPage(1);
    },
    draft,
    setDraft,
    tasks: tasksQuery.data?.items ?? [],
    tasksTotal: tasksQuery.data?.total ?? 0,
    teleSalesItems: teleSalesQuery.data?.items ?? [],
    teleSalesTotal: teleSalesQuery.data?.total ?? 0,
    teleSalesPublisherOptions: teleSalesPublishersQuery.data ?? [],
    teleSalesOriginalOwnerOptions: teleSalesOriginalOwnersQuery.data ?? [],
    strongFollowItems: strongFollowQuery.data?.items ?? [],
    strongFollowTotal: strongFollowQuery.data?.total ?? 0,
    strongFollowPublisherOptions: strongFollowPublishersQuery.data ?? [],
    strongFollowOriginalOwnerOptions: strongFollowOriginalOwnersQuery.data ?? [],
    myContacts: myContactsQuery.data?.items ?? [],
    myItemsTotal: myContactsQuery.data?.total ?? 0,
    myContactsTotal: myContactsCountsQuery.data?.all ?? 0,
    myContactsTeleSalesTotal: myContactsCountsQuery.data?.teleSales ?? 0,
    myContactsStrongFollowTotal: myContactsCountsQuery.data?.strongFollow ?? 0,
    myItemPublisherOptions: myItemPublishersQuery.data ?? [],
    selectedContactId,
    selectedContactDetail: contactDetailQuery.data,
    selectedContactRecords: contactRecordsQuery.data ?? [],
    contactMetadata: contactMetadataQuery.data,
    contactRecordLoading: contactDetailQuery.isLoading || contactRecordsQuery.isLoading,
    contactMutating: updateContactMutation.isPending,
    contactRecordMutating: createRecordMutation.isPending || deleteRecordMutation.isPending,
    claimingItemId,
    latestImportResult,
    staffOptions: staffOptionsQuery.data ?? [],
    staffOptionsLoading: staffOptionsQuery.isLoading,
    loading:
      staffOptionsQuery.isLoading ||
      tasksQuery.isLoading ||
      teleSalesQuery.isLoading ||
      strongFollowQuery.isLoading ||
      teleSalesPublishersQuery.isLoading ||
      strongFollowPublishersQuery.isLoading ||
      myContactsQuery.isLoading ||
      myContactsCountsQuery.isLoading ||
      myItemPublishersQuery.isLoading ||
      contactMetadataQuery.isLoading,
    mutating:
      createAndImportMutation.isPending ||
      confirmImportMutation.isPending ||
      publishMutation.isPending ||
      cancelMutation.isPending ||
      claimMutation.isPending ||
      batchClaimMutation.isPending ||
      ownerBatchClaimMutation.isPending ||
      releaseMutation.isPending ||
      releaseBatchMutation.isPending ||
      ownerBatchReleaseMutation.isPending ||
      returnToPoolMutation.isPending ||
      returnBatchMutation.isPending ||
      ownerBatchReturnMutation.isPending ||
      createRecordMutation.isPending ||
      updateContactMutation.isPending ||
      deleteRecordMutation.isPending,
    errorMessage,
    handleCreateAndImport: () => createAndImportMutation.mutateAsync(),
    handleConfirmImport: () => confirmImportMutation.mutateAsync(),
    handlePublishTask: (taskId: number) => publishMutation.mutateAsync(taskId),
    handleCancelTask: (taskId: number) => cancelMutation.mutateAsync(taskId),
    handleClaimItem,
    batchClaiming,
    ownerBatchClaiming,
    releaseBatching,
    releaseMutating: releaseMutation.isPending || releaseBatchMutation.isPending,
    ownerBatchReleasing,
    ownerBatchReturning,
    returnBatching,
    returningItemId: returnToPoolMutation.isPending ? returnToPoolMutation.variables : undefined,
    handleReturnItemToPool: (itemId: number) => returnToPoolMutation.mutateAsync(itemId),
    handleReturnItemsToPool: async (itemIds: number[]) => {
      if (
        returnBatchInFlightRef.current ||
        ownerBatchReturnInFlightRef.current ||
        releaseBatchInFlightRef.current ||
        ownerBatchReleaseInFlightRef.current ||
        itemIds.length === 0
      ) {
        return;
      }
      returnBatchInFlightRef.current = true;
      setReturnBatching(true);
      try {
        await returnBatchMutation.mutateAsync(itemIds);
      } catch {
        // 错误由 mutation 状态统一展示，避免事件处理器产生未处理 Promise。
      } finally {
        returnBatchInFlightRef.current = false;
        setReturnBatching(false);
      }
    },
    handleReturnItemsToPoolByOwner: async (ownerUserIds: number[]) => {
      if (
        ownerBatchReturnInFlightRef.current ||
        returnBatchInFlightRef.current ||
        releaseBatchInFlightRef.current ||
        ownerBatchReleaseInFlightRef.current ||
        ownerUserIds.length === 0
      ) {
        return;
      }
      ownerBatchReturnInFlightRef.current = true;
      setOwnerBatchReturning(true);
      try {
        await ownerBatchReturnMutation.mutateAsync(ownerUserIds);
      } catch {
        // 错误由 mutation 状态统一展示，避免事件处理器产生未处理 Promise。
      } finally {
        ownerBatchReturnInFlightRef.current = false;
        setOwnerBatchReturning(false);
      }
    },
    handleOpenContactRecords: (contactId: number) => setSelectedContactId(contactId),
    handleCloseContactRecords: () => setSelectedContactId(undefined),
    refreshMyContacts: () =>
      queryClient.invalidateQueries({
        queryKey: crmCollaborationTaskQueryRootKey,
      }),
    handleCreateRecord: (payload: ContactWorkbenchRecordCreatePayload) => {
      if (!selectedContactId) return Promise.resolve();
      return createRecordMutation.mutateAsync({
        contactId: selectedContactId,
        payload: {
          ...payload,
          recordedByUserId: payload.recordedByUserId ?? currentUserId,
        },
      });
    },
    handleUpdateContact: (payload: ContactWorkbenchUpdatePayload) => {
      if (!selectedContactId) return Promise.resolve();
      return updateContactMutation.mutateAsync({
        contactId: selectedContactId,
        payload,
      });
    },
    searchOrganizations(keyword: string) {
      return searchContactWorkbenchOrganizations({ keyword, limit: 50 });
    },
    searchOrganizationUnits(orgId: number, keyword: string) {
      return searchContactWorkbenchOrganizationUnits({ orgId, keyword, limit: 50 });
    },
    handleDeleteRecord: (recordId: number) => {
      if (!selectedContactId) return Promise.resolve();
      return deleteRecordMutation.mutateAsync({
        contactId: selectedContactId,
        recordId,
        currentUserId,
      });
    },
    handleClaimItems: async (itemIds: number[]) => {
      if (
        claimInFlightItemIdRef.current !== undefined ||
        batchClaimInFlightRef.current ||
        ownerBatchClaimInFlightRef.current ||
        releaseBatchInFlightRef.current ||
        ownerBatchReleaseInFlightRef.current ||
        itemIds.length === 0
      ) {
        return;
      }
      batchClaimInFlightRef.current = true;
      setBatchClaiming(true);
      try {
        await batchClaimMutation.mutateAsync(itemIds);
      } catch {
        // 错误由 mutation 状态统一展示，避免事件处理器产生未处理 Promise。
      } finally {
        batchClaimInFlightRef.current = false;
        setBatchClaiming(false);
      }
    },
    handleClaimItemsByOwner: async (
      poolCode: "tele_sales_pool" | "strong_follow_pool",
      ownerUserId: number | number[],
    ) => {
      const ownerUserIds = Array.isArray(ownerUserId) ? ownerUserId : [ownerUserId];
      if (
        claimInFlightItemIdRef.current !== undefined ||
        batchClaimInFlightRef.current ||
        ownerBatchClaimInFlightRef.current ||
        releaseBatchInFlightRef.current ||
        ownerBatchReleaseInFlightRef.current ||
        ownerUserIds.length === 0
      ) {
        return;
      }
      ownerBatchClaimInFlightRef.current = true;
      setOwnerBatchClaiming(true);
      try {
        await ownerBatchClaimMutation.mutateAsync({ poolCode, ownerUserIds });
      } catch {
        // 错误由 mutation 状态统一展示，避免事件处理器产生未处理 Promise。
      } finally {
        ownerBatchClaimInFlightRef.current = false;
        setOwnerBatchClaiming(false);
      }
    },
    handleReleaseItem: async (taskId: number, itemId: number) => {
      if (
        releaseBatchInFlightRef.current ||
        claimInFlightItemIdRef.current !== undefined ||
        batchClaimInFlightRef.current ||
        ownerBatchClaimInFlightRef.current ||
        ownerBatchReleaseInFlightRef.current
      ) {
        return;
      }
      await releaseMutation.mutateAsync({ taskId, itemIds: [itemId] });
    },
    handleReleaseItems: async (items: Array<{ taskId: number; itemId: number }>) => {
      if (
        releaseBatchInFlightRef.current ||
        claimInFlightItemIdRef.current !== undefined ||
        batchClaimInFlightRef.current ||
        ownerBatchClaimInFlightRef.current ||
        ownerBatchReleaseInFlightRef.current ||
        items.length === 0
      ) {
        return;
      }
      releaseBatchInFlightRef.current = true;
      setReleaseBatching(true);
      try {
        await releaseBatchMutation.mutateAsync(items);
      } catch {
        // 错误由 mutation 状态统一展示，避免事件处理器产生未处理 Promise。
      } finally {
        releaseBatchInFlightRef.current = false;
        setReleaseBatching(false);
      }
    },
    handleReleaseItemsByOwner: async (ownerUserIds: number[]) => {
      if (
        ownerBatchReleaseInFlightRef.current ||
        releaseBatchInFlightRef.current ||
        claimInFlightItemIdRef.current !== undefined ||
        batchClaimInFlightRef.current ||
        ownerBatchClaimInFlightRef.current ||
        ownerUserIds.length === 0
      ) {
        return;
      }
      ownerBatchReleaseInFlightRef.current = true;
      setOwnerBatchReleasing(true);
      try {
        await ownerBatchReleaseMutation.mutateAsync(ownerUserIds);
      } catch {
        // 错误由 mutation 状态统一展示，避免事件处理器产生未处理 Promise。
      } finally {
        ownerBatchReleaseInFlightRef.current = false;
        setOwnerBatchReleasing(false);
      }
    },
    appendRawText: (text: string) =>
      setDraft((current) => ({
        ...current,
        rawText: appendTextBlock(current.rawText, text),
      })),
  };
}

/** 追加上传文件解析出的 ID 文本，避免覆盖已经粘贴的内容。 */
function appendTextBlock(current: string, next: string) {
  const normalizedNext = next.trim();
  if (!normalizedNext) {
    return current;
  }
  const normalizedCurrent = current.trim();
  return normalizedCurrent ? `${normalizedCurrent}\r\n${normalizedNext}` : normalizedNext;
}
